/*
 * Database API Generator is a utility library for Thunderstorm.
 *
 * Given proper configurations it will dynamically generate APIs to your Firestore
 * collections, will assert uniqueness and restrict deletion... and more
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { _EmptyQuery } from '@nu-art/firebase-shared';
import { BadImplementationException } from '@nu-art/ts-common';
import { ModuleFE_BaseDB } from './ModuleFE_BaseDB.js';
import { DBApiDefGeneratorIDBV3 } from '../../shared.js';
import { apiWithBody, apiWithQuery } from '../../core/typed-api.js';
import { ModuleSyncType } from './types.js';
/**
 * Base API module for frontend database operations
 *
 * Provides core functionality for database CRUD operations through API endpoints
 * including support for operation queueing and request management.
 *
 * @template Proto - Database protocol type extending DBProto
 * @template _Config - Base configuration type
 * @template Config - Extended configuration type with DBApiFEConfig
 */
export class ModuleFE_BaseApi extends ModuleFE_BaseDB {
    // @ts-ignore
    v1;
    operations = {};
    /**
     * Creates an instance of ModuleFE_BaseApi
     *
     * @param dbDef - Database definition
     * @param defaultDispatcher - Default dispatcher for events
     * @param version - Optional API version string
     * @param customMemCreators - Optional custom memory creators for the database
     */
    constructor(dbDef, defaultDispatcher, version, customMemCreators) {
        super(dbDef, defaultDispatcher, ModuleSyncType.APISync, customMemCreators);
        const apiDef = this.resolveApiDef(dbDef, version);
        const _query = apiWithBody(apiDef.v1.query, (response) => this.onQueryReturned(response));
        const queryUnique = apiWithQuery(apiDef.v1.queryUnique, this.onGotUnique);
        const upsert = apiWithBody(apiDef.v1.upsert, async (item, original) => {
            const toRet = await this.onEntryUpdated(item, JSON.parse(original));
            this.IDB.setLastUpdated(item.__updated);
            return toRet;
        });
        const patch = apiWithBody(apiDef.v1.patch, (items) => this.onEntryPatched(items));
        const _delete = apiWithQuery(apiDef.v1.delete, this.onEntryDeleted);
        // @ts-ignore
        this.v1 = {
            query: (query) => _query(query || _EmptyQuery),
            // @ts-ignore
            queryUnique: (_id) => {
                return queryUnique({ _id });
            },
            // @ts-ignore
            upsert: (toUpsert) => {
                toUpsert = this.cleanUp(toUpsert);
                this.validateInternal(toUpsert);
                return this.updatePending(toUpsert, upsert(toUpsert), 'upsert');
            },
            upsertAll: apiWithBody(apiDef.v1.upsertAll, async (items) => {
                const toRet = await this.onEntriesUpdated(items);
                const lastUpdated = items.reduce((toRet, current) => Math.max(toRet, current.__updated), -1);
                this.IDB.setLastUpdated(lastUpdated);
                return toRet;
            }),
            // @ts-ignore
            patch: (toPatch) => {
                return this.updatePending(toPatch, patch(toPatch), 'patch');
            },
            delete: (item) => {
                return this.updatePending(item, _delete(item), 'delete');
            },
            deleteQuery: apiWithBody(apiDef.v1.deleteQuery, this.onEntriesDeleted),
            deleteAll: apiWithQuery(apiDef.v1.deleteAll),
        };
    }
    /**
     * Resolves the API definition for the database
     *
     * @param dbDef - Database definition
     * @param version - Optional API version
     * @returns Generated API definition for the database
     */
    resolveApiDef(dbDef, version) {
        return DBApiDefGeneratorIDBV3(dbDef, version);
    }
    /**
     * Cleans up data before upserting to database
     *
     * @param toUpsert - Data to be upserted
     * @returns Cleaned up data
     */
    cleanUp = (toUpsert) => {
        return toUpsert;
    };
    updatePending(item, request, requestType) {
        const id = item._id;
        if (id === undefined)
            return request;
        const _execute = request.execute.bind(request);
        request.execute = (onSuccess, onError) => {
            const operation = this.operations[id];
            if (!operation) {
                this.operations[id] = { running: { request, requestType } };
                // @ts-ignore
                // this.logInfo(`pre-executing operation(${requestType}) for ${id}: ${item.label}`);
                return _execute((r) => {
                    // @ts-ignore
                    // this.logInfo(`executing operation(${requestType}) for ${id}: ${item.label}`);
                    const pending = this.operations[id].pending;
                    delete this.operations[id];
                    if (!pending)
                        return onSuccess?.(r);
                    pending.request.execute(pending.onSuccess, pending.onError);
                }, (e) => {
                    delete this.operations[id];
                    onError?.(e);
                });
            }
            const runningRequestType = operation.running.requestType;
            const pendingRequestType = operation.pending?.requestType;
            if (runningRequestType === 'delete' || pendingRequestType === 'delete') {
                throw new BadImplementationException(`Item with id: ${id} is marked for deletion`);
            }
            if (runningRequestType === 'upsert' || runningRequestType === 'patch') {
                if (operation.pending) { // @ts-ignore
                    // this.logInfo(`canceling pending operation(${operation.pending.requestType}) for ${id}`);
                }
                // @ts-ignore
                // this.logInfo(`scheduling pending operation(${requestType}) for ${id}: ${item.label}`);
                operation.pending = { request, requestType, onSuccess, onError };
                operation.running.request.clearOnCompleted();
            }
            return request;
        };
        return request;
    }
}
//# sourceMappingURL=ModuleFE_BaseApi.js.map